import { create } from 'zustand';
import { getToday, type TodayResponse } from '@/api/today';

/**
 * Today, held once for the app: the shell loads it beside the overview (the
 * sidebar shows its count) and reloads it on the same triggers — focus and
 * `orders.updated` — and the Today page reads from here. The server rebuilds
 * the order packets on every read, so a reload is also how a changed draft
 * reaches its packet.
 */
interface TodayState {
  today: TodayResponse | null;
  loading: boolean;
  error: string | null;
  lastLoadedAt: number;
  /** The page's Late section is open (default); kept while the app is open. */
  lateOpen: boolean;
  setLateOpen: (open: boolean) => void;
  load: () => Promise<void>;
  /** Reload only if older than `maxAgeMs` (focus); events and taps call load. */
  refreshIfStale: (maxAgeMs: number) => void;
}

let inFlight: Promise<void> | null = null;

export const useTodayStore = create<TodayState>()((set, get) => ({
  today: null,
  loading: false,
  error: null,
  lastLoadedAt: 0,
  lateOpen: true,
  setLateOpen: (lateOpen) => set({ lateOpen }),
  refreshIfStale: (maxAgeMs) => {
    if (Date.now() - get().lastLoadedAt > maxAgeMs) void get().load();
  },
  load: () => {
    if (inFlight) return inFlight;
    set({ loading: true });
    inFlight = getToday()
      .then((today) => set({ today, error: null, lastLoadedAt: Date.now() }))
      .catch((error: unknown) => set({ error: error instanceof Error ? error.message : String(error) }))
      .finally(() => { set({ loading: false }); inFlight = null; });
    return inFlight;
  },
}));
